import { useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import TabsComponent from "@/components/tabs/tabs-component";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";

import {
  JobOrdersDetailDynamic,
  JobOrdersDynamicAttribute,
  UpdateJobOrdersDynamic
} from "@/hooks/services/pps/handlers/job-orders-dynamic-api";

import { JobInformationContent } from "./job-information-content";

import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";

import { useWorkSpaceStore } from "@/stores/store";

import { JobOrderAttributeDataType } from "../../list/constant";
import { ProcessContent } from "./process-content";
import { toastManager } from "@/lib/toast-manager";

const fieldName = {
  joborderno: "joborderno",
  part: "part"
};

export interface OrganizeData {
  headerItems: { label: string; value: any; valueId?: any; name: string }[];
  overviewItems: {
    label: string;
    value: any;
    valueId?: any; // ID for foreign keys
    attribute?: string;
    dataType?: number;
    isSystemAttribute?: boolean;
    isEditable?: boolean;
    nestedColumnName?: string;
    referenceTable?: string;
  }[];
  dateTimeItems: {
    label: string;
    value: any;
    valueId?: any; // ID for foreign keys
    attribute?: string;
    dataType?: number;
    isSystemAttribute?: boolean;
    isEditable?: boolean;
    nestedColumnName?: string;
    referenceTable?: string;
  }[];
  relationshipItems: {
    label: string;
    value: any;
    valueId?: any; // ID for foreign keys
    attribute?: string;
    dataType?: number;
    isSystemAttribute?: boolean;
    isEditable?: boolean;
    nestedColumnName?: string;
    referenceTable?: string;
  }[];
  otherItems: {
    label: string;
    value: any;
    valueId?: any; // ID for foreign keys
    attribute?: string;
    dataType?: number;
    isSystemAttribute?: boolean;
    isEditable?: boolean;
  }[];
}

interface DynamicSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: number;
  refetch: () => void;
  canManage?: boolean;
  initialTab?: JobOrderDrawerInitialTab;
  workflowMode?: JobOrderWorkflowMode;
}

export type JobOrderDrawerInitialTab = "job-information" | "process";
export type JobOrderWorkflowMode = "view" | "edit";

const DETAIL_REFRESH_INTERVAL_DEFAULT_MS = 15_000;
const DETAIL_REFRESH_INTERVAL_WORKFLOW_MS = 5_000;

function DynamicSheetContent({
  open,
  onOpenChange,
  id,
  refetch,
  canManage = true,
  initialTab = "job-information",
  workflowMode = "view"
}: DynamicSheetProps) {
  const { workspace } = useWorkSpaceStore();
  const [activeTab, setActiveTab] =
    useState<JobOrderDrawerInitialTab>(initialTab);
  const hasValidJobOrderId = id > 0;
  const filterParams = {
    id,
    ...(workspace?.id && { workspaceId: workspace?.id })
  };

  const updateJobOrdersDynamicById = usePutData(UpdateJobOrdersDynamic());

  const extractForeignKeyId = (value: any) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "object") {
      if (value.id !== undefined && value.id !== null) {
        return value.id;
      }

      if (value.valueId !== undefined && value.valueId !== null) {
        return value.valueId;
      }

      return null;
    }

    return value;
  };

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const jobOrderDetailDynamic = useGetData(
    JobOrdersDetailDynamic({
      ...filterParams
    }),
    {
      enabled: Boolean(open && hasValidJobOrderId)
    }
  );
  const refetchJobOrderDetail = jobOrderDetailDynamic.refetch;

  const shouldRefreshJobDetail = open && hasValidJobOrderId;
  const isWorkflowTabOpen = activeTab === "process";

  useEffect(() => {
    if (!shouldRefreshJobDetail) {
      return;
    }

    // Ensure freshly opened sheet or workflow tab always loads latest Job EDD.
    void refetchJobOrderDetail();
  }, [shouldRefreshJobDetail, isWorkflowTabOpen, refetchJobOrderDetail]);

  useEffect(() => {
    if (!shouldRefreshJobDetail) {
      return;
    }

    const refreshInterval = isWorkflowTabOpen
      ? DETAIL_REFRESH_INTERVAL_WORKFLOW_MS
      : DETAIL_REFRESH_INTERVAL_DEFAULT_MS;

    const intervalId = window.setInterval(() => {
      void refetchJobOrderDetail();
    }, refreshInterval);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [shouldRefreshJobDetail, isWorkflowTabOpen, refetchJobOrderDetail]);

  const dynamicJobAttribute = useGetData(
    JobOrdersDynamicAttribute({
      ...filterParams,
      isActive: true
    }),
    {
      enabled: Boolean(open && hasValidJobOrderId)
    }
  );

  const nestedValue = useCallback(
    (item: any) => {
      const detail = jobOrderDetailDynamic?.data?.data;
      if (item.nestedColumnName) {
        if (item.nestedType === "object") {
          return detail[item.name]?.[item.nestedColumnName];
        } else if (item.nestedType === "array") {
          if (!detail[item.name] || !Array.isArray(detail[item.name])) {
            return "";
          }
          return detail[item.name]
            .map((subItem: any) => subItem[item.nestedColumnName])
            .join(", ");
        }
      }
      return detail[item.name];
    },
    [jobOrderDetailDynamic?.data?.data]
  );

  const processData = useCallback((): OrganizeData => {
    if (
      !dynamicJobAttribute?.data?.data ||
      !jobOrderDetailDynamic?.data?.data
    ) {
      return {
        headerItems: [],
        overviewItems: [],
        dateTimeItems: [],
        relationshipItems: [],
        otherItems: []
      };
    }

    const headerItems: {
      label: string;
      value: any;
      valueId?: any;
      name: string;
    }[] = [];
    const overviewItems: {
      label: string;
      value: any;
      attribute?: string;
      dataType?: number;
      isSystemAttribute?: boolean;
      isEditable?: boolean;
      nestedColumnName?: string;
      referenceTable?: string;
    }[] = [];
    const dateTimeItems: {
      label: string;
      value: any;
      attribute?: string;
      dataType?: number;
      isSystemAttribute?: boolean;
      isEditable?: boolean;
      nestedColumnName?: string;
      referenceTable?: string;
    }[] = [];
    const relationshipItems: {
      label: string;
      value: any;
      attribute?: string;
      dataType?: number;
      isSystemAttribute?: boolean;
      isEditable?: boolean;
      nestedColumnName?: string;
      referenceTable?: string;
    }[] = [];
    const otherItems: {
      label: string;
      value: any;
      attribute?: string;
      dataType?: number;
      isSystemAttribute?: boolean;
      isEditable?: boolean;
    }[] = [];

    const headerFieldNames = [fieldName.joborderno, fieldName.part];

    dynamicJobAttribute.data.data.forEach((item: any) => {
      const value = nestedValue(item);

      // For FOREIGN_KEY, we need both the ID (from parent field) and display value (from nested field)
      const detail = jobOrderDetailDynamic?.data?.data;
      const valueId =
        item.dataType === JobOrderAttributeDataType.FOREIGN_KEY && item.name
          ? extractForeignKeyId(detail[item.name])
          : value; // For non-foreign-key, valueId = value

      const itemData = {
        label: item.label,
        value: value, // Display value (label/text)
        valueId: valueId, // ID value for foreign keys
        attribute: item.name,
        dataType: item.dataType,
        isSystemAttribute: item.isSystemAttribute === true,
        isEditable: item.isEditable !== false,
        nestedColumnName: item.nestedColumnName,
        referenceTable: item.referenceTable
      };

      // header fields (joborderno, part)
      if (headerFieldNames.includes(item.name?.toLowerCase())) {
        const headerName =
          item.name?.toLowerCase() === fieldName.part
            ? fieldName.part
            : item.name?.toLowerCase();

        if (!headerItems.some((headerItem) => headerItem.name === headerName)) {
          headerItems.push({
            label: item.label,
            value: value,
            valueId,
            name: headerName
          });
        }
      }
      // DATETIME fields
      if (item.dataType === JobOrderAttributeDataType.DATETIME) {
        dateTimeItems.push(itemData);
      }
      // FOREIGN_KEY fields (relationships)
      else if (item.dataType === JobOrderAttributeDataType.FOREIGN_KEY) {
        relationshipItems.push(itemData);
      }
      // Basic overview fields (TEXT, INT, BOOLEAN, etc.)
      else if (
        item.dataType === JobOrderAttributeDataType.TEXT ||
        item.dataType === JobOrderAttributeDataType.INT ||
        item.dataType === JobOrderAttributeDataType.FRACTION ||
        item.dataType === JobOrderAttributeDataType.BOOLEAN
      ) {
        overviewItems.push(itemData);
      }
      // Everything else
      else {
        otherItems.push(itemData);
      }
    });

    return {
      headerItems,
      overviewItems,
      dateTimeItems,
      relationshipItems,
      otherItems
    };
  }, [
    dynamicJobAttribute?.data?.data,
    jobOrderDetailDynamic?.data?.data,
    nestedValue
  ]);

  const organizedData: OrganizeData = processData();

  const jobOrderEdd = useMemo(() => {
    const dateTimeEdd = organizedData.dateTimeItems.find((item) => {
      const normalizedAttribute = item.attribute?.toLowerCase();
      const normalizedLabel = item.label?.toLowerCase();

      return normalizedAttribute === "edd" || normalizedLabel === "edd";
    });

    if (dateTimeEdd?.value) {
      return dateTimeEdd.value;
    }

    return jobOrderDetailDynamic?.data?.data?.edd ?? null;
  }, [jobOrderDetailDynamic?.data?.data?.edd, organizedData.dateTimeItems]);

  const getHeaderValue = (fieldName: string) => {
    const field = organizedData.headerItems.find(
      (item) => item.name === fieldName
    );
    return field?.value || "N/A";
  };

  const getHeaderValueId = (fieldName: string) => {
    const field = organizedData.headerItems.find(
      (item) => item.name === fieldName
    );

    return field?.valueId ?? null;
  };

  const parsedPartId = Number(
    getHeaderValueId(fieldName.part) ??
      extractForeignKeyId(jobOrderDetailDynamic?.data?.data?.part)
  );
  const jobOrderPartId =
    Number.isFinite(parsedPartId) && parsedPartId > 0 ? parsedPartId : null;

  const handleJobInfoSave = (
    body: Record<string, string | number | boolean>
  ) => {
    // Get the field name for more specific feedback
    const fieldName = Object.keys(body)[0];

    updateJobOrdersDynamicById.mutate(
      { body, id },
      {
        onSuccess: () => {
          toastManager.showSuccess(
            "✨ Field Updated Successfully",
            `${fieldName} has been updated successfully.`
          );
          jobOrderDetailDynamic.refetch();
          if (refetch) refetch();
        },
        onError: (error) => {
          toastManager.showError(
            "❌ Update Failed",
            error?.message || `Failed to update ${fieldName}`
          );
        }
      }
    );
  };

  const tabs = [
    {
      label: "Overview",
      value: "job-information",
      content: (
        <JobInformationContent
          organizedData={organizedData}
          onSave={handleJobInfoSave}
          loading={updateJobOrdersDynamicById.isPending}
          canManage={canManage} // WOR-1793 Req #10: Pass canManage to Job Information
        />
      ),
      badge: (
        organizedData.overviewItems.length +
        organizedData.dateTimeItems.length +
        organizedData.relationshipItems.length +
        organizedData.otherItems.length
      ).toString()
    },
    {
      label: "Workflow",
      value: "process",
      content: (
        <ProcessContent
          jobOrder={{
            id,
            jobOrderNo: getHeaderValue(fieldName.joborderno),
            partId: jobOrderPartId
          }}
          jobEdd={jobOrderEdd}
          isDrawerOpen={open}
          initialWorkflowMode={workflowMode}
          canManage={canManage} // WOR-1793 Req #11-12: Pass canManage to Process
        />
      )
    }
  ];

  const handleOpenChange = (isOpen: boolean) => {
    // Refresh the job orders list when the drawer closes so the table reflects
    // the latest current process and assignments.
    if (!isOpen) refetch();
    onOpenChange(isOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "aero:sm:max-w-[600px] aero:p-0 aero:gap-0",
          "aero:flex aero:flex-col"
        )}
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:p-6 aero:pb-0 aero:gap-2">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Job Order Details
            </SheetTitle>
            <div className="aero:flex aero:items-center aero:gap-2">
              {/* <Button
                intent="secondary"
                design="outline"
                type="button"
                size="md"
                className="aero:h-10 aero:gap-2 aero:px-4"
                disabled
              >
                <User className="aero:w-5 aero:h-5" />
                <span className="aero:font-bold aero:text-sm">Assignment</span>
              </Button> */}
              <SheetClose asChild>
                <button className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100">
                  <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
                </button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:pt-5">
          {jobOrderDetailDynamic.isLoading || dynamicJobAttribute.isLoading ? (
            <div className="aero:flex aero:flex-col aero:items-center aero:justify-center aero:py-16 aero:text-center">
              <div className="aero:h-12 aero:w-12 aero:rounded-xl aero:bg-neutral-100 aero:flex aero:items-center aero:justify-center aero:mb-4">
                <div className="aero:h-6 aero:w-6 aero:border-2 aero:border-brand-primary-500 aero:border-t-transparent aero:rounded-full aero:animate-spin"></div>
              </div>
              <h3 className="aero:text-base aero:font-bold aero:text-foreground aero:mb-2">
                Loading Job Details
              </h3>
              <p className="aero:text-sm aero:text-neutral-500 aero:max-w-sm">
                Please wait while we fetch the latest job order information...
              </p>
            </div>
          ) : (
            <TabsComponent
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabClassName="aero:border-b-1"
              scrollable={false}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default DynamicSheetContent;
